/*
 * Home page
 *
 * Landing page. Responsibilities:
 * - Present hero content and CTAs linking to schedule / about pages.
 * - Render a responsive carousel of member reviews with "Read more" toggles
 *   when content is too long.
 *
 * Notes / behaviors:
 * - Truncation detection compares an element's scrollHeight and clientHeight
 *   to decide whether to show a "Read more" control. Relies on refs and
 *   runs on resize to stay accurate across responsive breakpoints.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, Button, Container, Stack, Divider, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ReactComponent as LogoSVG } from '../components/LogoNoText.svg';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Hero image sources 
const HERO_URLS = [
  'https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763765613/1000011127_yny78c.jpg',
  'https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763765610/1000017846_x6ddoa.jpg',
  'https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763765606/FB_IMG_1686950302730_pkwnoq.jpg',
  'https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763765605/FB_IMG_1686951145912_jy3uqs.jpg',
];

function Home() {
  const reviews = useMemo(() => [
    {
      name: 'Amrit Aujla',
      content: `This gym has been a huge part of my growth as a fighter. I’ve trained at quite a few gyms, and this one stands out. The coaches are knowledgeable, experienced, and genuinely care about helping you improve. They’re detail-oriented, give honest feedback, and actually coach you by being present, engaged, and invested in your progress.

The training is solid no matter your level. Whether you're just starting out or have fight experience, you'll be challenged and supported. I also really appreciate how clean and well-maintained the space is, it really makes a difference.

If you’re serious about getting better and want quality coaching, this is the place.`,
      footer: '— Amrit A.',
      link: 'https://g.co/kgs/Dqjq2Hb',
    },

        {
      name: 'Gareth Jones',
      content: 'Excellent atmosphere and instruction. The facility is clean, well staffed and well run. Large variety of classes and times.',
      footer: '— Gareth J.',
      link: 'https://maps.app.goo.gl/YikUCvmScEtqy3Rh6',
    },

        {
      name: 'Jesshi Lau.',
      content: `I've been training at Kingdom MMA for three years, and it’s one of the best decisions I’ve made. This gym is a great for anyone looking to compete or improve their skills in a supportive, high-energy environment. 
      
      The coaches are top-notch, offering expert guidance and pushing you to reach your full potential. What stands out most is the incredible variety of classes with a wide schedule that fits even the busiest lifestyles. 
      
      Kingdom MMA has everything you need to succeed, and I can’t recommend it enough for anyone serious about martial arts!`,
      footer: '— Jesshi L.',
      link: 'https://maps.app.goo.gl/uPfu8AkQeEV6ECfx6',
    },

        {
      name: 'Joe McFall V.',
      content: `I have been training at Kingdom MMA since they opened 9 years ago. The facility is always clean and organized.

The coaches are a top notch group of professionals with an extensive knowledge base, always patient, and willing to help. The coaches and other members are all very welcoming and willing to help you grow in the sport whether you are doing it for competition or just trying to stay in shape. They offer a wide range of class times for all skill levels.

I highly recommend Kingdom MMA as your go to gym for grappling or kickboxing training.`,
      footer: '— Joe M.',
      link: 'https://maps.app.goo.gl/1KWiXAYRQJUruTYQ6',
    },
    {
      name: 'Bill Killinger',
      content: `The owners and head coaches of this amazing facility have an extremely intelligent and well-thought out approach to developing their athletes. Whether you want to train for fun or compete, I guarantee you'll become better in combat sports. 
      
      As an older athlete, I've been around a lot of other gyms over the years. This gym and their staff have given me insights and lessons I hadn't seen anywhere else; with an abundance of knowledge, and more importantly, integrity.  Their facility is huge, with lots of ring/cage/mat space, and is well kept and clean. 
      
      I highly recommend Kingdom MMA for more reasons than I can list here.`,
      footer: '— Bill K.',
      link: 'https://maps.app.goo.gl/HUvCVmm6MF9XVKTd6',
    },


    {
      name: 'Mariana Nava Martinez.',
      content: `I have been training in Muay Thai at Kingdom for about 4 months, and my experience has been nothing short of amazing. The gym has a welcoming atmosphere, incredible coaches, and is always kept clean by the staff for everyone’s safety. 
      
      The coaches are approachable, always willing to answer questions, and give constructive feedback to help you improve - It is clear they are highly skilled and deeply knowledgeable in what they teach.

I would highly recommend Kingdom MMA!`,
      footer: '— Mariana N.',
      link: 'https://maps.app.goo.gl/2Fqj8BipSLzsPeoJA',
    },
    {
      name: 'Isaiah Bean.',
      content: `Kingdom MMA is arguably the best MMA gym in Edmonton. As an MMA fighter who has trained at various gyms, I can confidently say that I wish I had joined Kingdom sooner.

The instructors and members are incredibly knowledgeable and eager to teach. Everyone here fosters a positive environment that encourages improvement, even for beginners. They are patient with students and offer opportunities for one-on-one training with experienced martial artists at very reasonable prices.

For those interested in competition, Kingdom actively tracks events, tournaments, and smokers, ensuring fighters have opportunities to compete. They prioritize preparation and matching fighters appropriately. Instructors are always present to coach, and if unavailable, someone with great experience steps in to support the team.

What I love most about Kingdom is the sense of community. While MMA is often seen as an individual sport, training and competing here feels like being part of a supportive team. Members of all shapes and sizes train together, striving to help each other achieve their fitness and martial arts goals.`,
      footer: '— Isaiah B.',
      link: 'https://maps.app.goo.gl/zrW8qXeWRfSvFobM7',
    },
  ], []);

  // Responsive: how many reviews to show at once
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  let reviewsToShow = 1;
  if (isMd) reviewsToShow = 3;
  else if (isSm) reviewsToShow = 2;
  else if (!isXs) reviewsToShow = 4;

  // Carousel state
  const [startIdx, setStartIdx] = useState(0);
  const [expanded, setExpanded] = useState({}); // { [index]: true/false }
  const total = reviews.length;
  // Get visible reviews, wrap around
  const visibleReviews = useMemo(() => {
    return Array.from({ length: reviewsToShow }, (_, i) => {
      const idx = (startIdx + i) % total;
      return { ...reviews[idx], idx };
    });
  }, [startIdx, reviewsToShow, total, reviews]);

  // Handlers
  const handlePrev = () => setStartIdx((prev) => (prev - 1 + total) % total);
  const handleNext = () => setStartIdx((prev) => (prev + 1) % total);
  const handleExpand = (idx) => setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
    const ctaStyles = {
        background: 'linear-gradient(135deg, #ffffff 0%, #888888 100%)',
        color: '#121212',
        fontWeight: 'bold',
        fontSize: '1.1rem',
        px: 4,
        py: 1.5,
        borderRadius: 3,
        boxShadow: '0 4px 16px rgba(255,255,255,0.2)',
        whiteSpace: 'nowrap',
        minWidth: 'auto',
        '&:hover': {
        background: 'linear-gradient(135deg, #888888 0%, #ffffff 100%)',
        color: '#fff',
        boxShadow: '0 8px 32px rgba(255,255,255,0.3)',
        },
  };
  // Responsive review box height
  const reviewBoxBase = {
    backgroundColor: '#1a1a1a',
    borderRadius: 2,
    // keep vertical padding the same, increase horizontal padding on mobile by 0.5
    py: { xs: 2, sm: 3 },
    px: { xs: 3, sm: 3 },
    boxShadow: '0 2px 12px rgba(255,255,255,0.08)',
    textAlign: 'left',
    color: '#ccc',
    textDecoration: 'none',
    transition: 'transform 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: 0,
    overflow: 'hidden',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0 4px 24px rgba(255,255,255,0.12)',
    },
  };

  const reviewTextStyles = {
    fontSize: '1rem',
    lineHeight: 1.6,
  };

  const reviewFooterStyles = {
    mt: 2,
    color: '#888',
    fontSize: '0.9rem',
  };

  const [ref, inView] = useInView({ triggerOnce: true });

  // Hero image rotation: pick one of the Cloudinary images at random on mount.

  const [heroSrc, setHeroSrc] = useState(null);
  const [heroBlur, setHeroBlur] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroSrcSet, setHeroSrcSet] = useState(null);

  // Insert Cloudinary transform string after '/upload/' in a Cloudinary URL
  const injectTransform = (url, transform) => {
    try {
      const parts = url.split('/upload/');
      if (parts.length !== 2) return url;
      return parts[0] + '/upload/' + transform + '/' + parts[1];
    } catch (e) {
      return url;
    }
  };

  useEffect(() => {
    // Choose a random hero image on mount
    const idx = Math.floor(Math.random() * HERO_URLS.length);
    const base = HERO_URLS[idx];

    const widths = [640, 768, 1024, 1366, 1920, 2560];
    const srcset = widths
      .map((w) => {
        const h = Math.round((w * 9) / 16); // keep 16:9 aspect
        const url = injectTransform(base, `f_auto,q_auto,c_fill,w_${w},h_${h}`);
        return `${url} ${w}w`;
      })
      .join(', ');

    const fallback = injectTransform(base, 'f_auto,q_auto,c_fill,w_1920,h_1080');
    // LQIP blur placeholder with same 16:9 aspect ratio
    const blur = injectTransform(base, 'f_auto,q_10,w_32,h_18,e_blur:400');

    // Preload the hero image for better LCP
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = fallback;
    link.imageSrcset = srcset;
    link.imageSizes = '100vw';
    document.head.appendChild(link);

    setHeroBlur(blur);
    setHeroSrc(fallback);
    setHeroSrcSet(srcset);

    return () => {
      try {
        document.head.removeChild(link);
      } catch (e) {
        /* ignore */
      }
    };
  }, []);

  // For social media previews
  const ogImage = "https://res.cloudinary.com/dmu7vt5cs/image/upload/b_rgb:FFFFFF/l_image:upload:logov3_zcjcxc/c_scale,fl_relative,w_0.90/o_100/fl_layer_apply,g_center,x_0.03,y_0.04/v1763765610/1000017846_x6ddoa.jpg";
  
  // Track which reviews are truncated at current screen size
  const [truncated, setTruncated] = useState({});
  const reviewRefs = useRef([]);

  // Check if review content is truncated (visually overflows)
  useEffect(() => {
    const checkTruncation = () => {
      const newTruncated = {};
      visibleReviews.forEach((review, i) => {
        const ref = reviewRefs.current[review.idx];
        if (ref) {
          // Compare scrollHeight to clientHeight to detect overflow
          newTruncated[review.idx] = ref.scrollHeight > ref.clientHeight + 2; // +2 for rounding
        }
      });
      setTruncated(newTruncated);
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [visibleReviews, expanded, isXs, isSm, isMd]);

  return (
    <Box
      sx={{
        // Make the hero height responsive so it looks good on phones, tablets and desktop
        minHeight: { xs: '65vh', sm: '75vh', md: '85vh', lg: '90vh' },
        backgroundColor: '#111',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 4, sm: 6, md: 12 },
        px: { xs: 2, sm: 4, md: 6 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Helmet>
        <title>Kingdom MMA and Fitness | Edmonton MMA & Fitness Gym</title>
        <meta name="description" content="Join Kingdom MMA & Fitness in Edmonton for Muay Thai, BJJ, boxing and MMA. View our class schedule and start a 3‑day free trial today." />
        <meta name="keywords" content="Kingdom MMA Edmonton, MMA gym Edmonton, Muay Thai Kickboxing Edmonton, BJJ Edmonton, Boxing classes Edmonton, Wrestling training Edmonton" />
        <meta name="author" content="Kingdom MMA and Fitness" />
        <link rel="canonical" href="https://www.kingdommixedmartialarts.ca/" />
        <meta property="og:title" content="Kingdom MMA and Fitness | Edmonton" />
        <meta property="og:description" content="Join Kingdom MMA & Fitness in Edmonton for Muay Thai, BJJ, boxing and MMA. View our class schedule and start a 3‑day free trial today." />
        <meta property="og:url" content="https://www.kingdommixedmartialarts.ca/" />
  <meta property="og:image" content={ogImage} />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:image:alt" content="Kingdom MMA and Fitness — Edmonton training session and 3-day free trial" />
  <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Background Image with Overlay (randomized hero with LQIP + preload) */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
        {/* Low-quality blurred placeholder while the full image loads */}
        {heroBlur && !heroLoaded && (
          <Box
            component="img"
            src={heroBlur}
            alt=""
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scale(1.04)',
              filter: 'blur(8px) saturate(0.9)',
            }}
          />
        )}

        {/* Full resolution hero image (prioritized for LCP) */}
        {heroSrc && (
              <Box
                component="img"
                src={heroSrc}
                srcSet={heroSrcSet || undefined}
                sizes="100vw"
                alt="Kingdom MMA training session"
                loading="eager"
                onLoad={() => setHeroLoaded(true)}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 35%',
                  transition: 'opacity 360ms ease',
                  opacity: heroLoaded ? 1 : 0,
                }}
              />
        )}

        {/* Gradient overlay for contrast */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.9))',
          }}
        />
      </Box>

      {/* Main Content Container */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: { xs: 'center', md: 'left' },
          gap: { xs: 4, md: 8 },
        }}
      >
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <LogoSVG
            alt="Kingdom MMA and Fitness logo"
            style={{
              width: 'clamp(240px, 40vw, 500px)',
              height: 'auto',
              filter: 'drop-shadow(0 0 12px #aaa)',
              cursor: 'pointer',
            }}
            onClick={() => window.location.href = '/'}
          />
        </motion.div>

        {/* Divider under logo on mobile */}
        <Divider
          sx={{
            width: { xs: '80%', md: '0' },
            mx: 'auto',
            my: { xs: 3, md: 0 },
            borderColor: 'rgba(255,255,255,0.2)',
            display: { xs: 'block', md: 'none' },
          }}
        />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          style={{ flex: 1 }}
        >
          <Typography variant="h1" sx={{
            fontSize: 'clamp(1.8rem, 5vw, 2.4rem)',
            fontWeight: 700,
            mb: 2,
            textShadow: '0 2px 6px rgba(0,0,0,0.5)',
          }}>
            Kingdom MMA and Fitness
          </Typography>

          <Typography variant="h2" sx={{
            fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
            fontWeight: 400,
            mb: 2,
            color: '#bbb',
          }}>
            Mixed Martial Arts & Fitness Training in Edmonton
          </Typography>

          <Typography variant="h3" sx={{
            fontSize: 'clamp(1rem, 1.8vw, 1.3rem)',
            fontWeight: 400,
            mb: 3,
            color: '#ccc',
          }}>
            Muay Thai Kickboxing • Brazilian Jiu Jitsu • Boxing • Wrestling • MMA
          </Typography>

          <Typography variant="body1" sx={{
            fontSize: 'clamp(1rem, 1.1vw, 1.1rem)',
            lineHeight: 1.6,
            color: '#ccc',
            mb: 4,
            maxWidth: '700px',
            mx: { xs: 'auto', md: 0 },
          }}>
            Welcome to <strong>Kingdom MMA and Fitness</strong> — your home for Mixed Martial Arts and fitness in Edmonton. Whether you're a beginner or a competitive athlete, our expert coaches offer elite training in <strong>Muay Thai Kickboxing</strong>, <strong>Brazilian Jiu Jitsu (BJJ)</strong>, <strong>Boxing</strong>, <strong>Wrestling</strong>, and <strong>MMA</strong>. Join our inclusive community and start your journey with a{' '}
            <Box component="a" href="/memberships#3-day-free-trial" sx={{
              fontWeight: 'bold',
              textDecoration: 'underline',
              color: '#fff',
              '&:hover': { color: '#aaa' },
            }}>
              3-day free trial
            </Box>.
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'row', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent={{ xs: 'center', md: 'flex-start' }}
            alignItems="center"
            sx={{ flexWrap: 'nowrap' }}
          >
            <Button
              variant="contained"
              size="large"
              href="/schedule#our-schedule"
              sx={{
                ...ctaStyles,
                px: { xs: 2, sm: 4 },
                py: { xs: 0.6, sm: 1.5 },
                fontSize: { xs: '0.85rem', sm: '1.1rem' },
                minWidth: 0,
              }}
            >
              View Schedule
            </Button>
            <Button
              variant="contained"
              size="large"
              href="/about#our-coaches"
              sx={{
                ...ctaStyles,
                px: { xs: 2, sm: 4 },
                py: { xs: 0.6, sm: 1.5 },
                fontSize: { xs: '0.85rem', sm: '1.1rem' },
                minWidth: 0,
              }}
            >
              Meet the Coaches
            </Button>
          </Stack>
        </motion.div>
      </Container>

        {/* Spacer to push content down */} 
        <Box
        sx={{
            height: '20vh', 
            width: '100%',}}
        />

      {/* Divider before reviews */}
      <Divider
        sx={{
          mt: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 },
          width: '60%',
          mx: 'auto',
          borderColor: 'rgba(255,255,255,0.1)',
        }}
      />

      
        <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        >
            {/* Review Section */}
        <Box sx={{
  zIndex: 1,
  position: 'relative',
        background: 'linear-gradient(to top, #121212, #0a0a0a)',
        py: { xs: 6, md: 8 },
        px: { xs: 2, sm: 4 },
        // Negate parent horizontal padding on mobile so reviews can use more width
        mx: { xs: -2, sm: 0 },
  // expand slightly on xs to fully occupy viewport
  width: { xs: 'calc(100% + 32px)', sm: '100%' },
        textAlign: 'center',
      }}>
                <Typography
                variant="h2"
                sx={{
                    fontSize: 'clamp(1.4rem, 2vw, 2rem)',
                    fontWeight: 600,
                    mb: 4,
                    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }}
                >
                What Our Members Say
                </Typography>

                {/* Carousel with arrows */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 5, position: 'relative' }}>
        <IconButton
          aria-label="previous"
          onClick={handlePrev}
          sx={{
            color: '#fff',
            mx: { xs: 0.5, sm: 1 },
            // On mobile position arrows as overlays to avoid being pushed off-screen
            position: { xs: 'absolute', sm: 'relative' },
            // overlap the review box and extend further outside the edge
            left: { xs: -20, sm: 'auto' },
            top: { xs: '50%', sm: 'auto' },
            transform: { xs: 'translateY(-50%)', sm: 'none' },
            zIndex: 3,
            // make circular background transparent on mobile
            bgcolor: { xs: 'transparent', sm: 'transparent' },
            '&:hover': { bgcolor: { xs: 'transparent', sm: 'transparent' } },
          }}
        >
          <ArrowBackIosNewIcon fontSize="large" />
        </IconButton>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
                    {visibleReviews.map((review, i) => {
                    const isExpanded = !!expanded[review.idx];
          // Make review boxes larger on mobile so more content is visible
          const boxHeight = isExpanded
            ? { height: 'auto', maxHeight: 'none' }
            : { height: { xs: 260, sm: 240, md: 260, lg: 280 }, maxHeight: { xs: 260, sm: 240, md: 260, lg: 280 } };
                    return (
                        <Box
            key={i}
            component="a"
            href={review.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...reviewBoxBase,
              ...boxHeight,
              minWidth: 0,
              flex: { xs: '0 0 90vw', sm: 1 },
              position: 'relative',
              cursor: 'pointer',
            }}
            onClick={e => {
              // Only expand/collapse if clicking the button, not the box link
              if (e.target.classList.contains('read-more-btn')) {
              e.preventDefault();
              handleExpand(review.idx);
              }
            }}
            >
                        <Typography sx={{ color: '#fff', mb: 1 }}>⭐⭐⭐⭐⭐</Typography>
                        <Typography
                            ref={el => (reviewRefs.current[review.idx] = el)}
                            sx={{
                            ...reviewTextStyles,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: isExpanded ? 'unset' : 5,
                            whiteSpace: isExpanded ? 'pre-line' : 'normal',
                            mb: 1,
                            }}
                        >
                            {`“${review.content.replace(/\n/g, '\n')}`}
                        </Typography>
                        {/* Show Read more if truncated or expanded */}
                        {!isExpanded && truncated[review.idx] && (
                            <Button
                            className="read-more-btn"
                            size="small"
                            variant="text"
                            sx={{
                              color: '#bbb',
                              textTransform: 'none',
                              fontWeight: 500,
                              p: 0,
                              minWidth: 0,
                              my: 1,
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              lineHeight: 1,
                              display: 'inline-block'
                            }}
                            >
                            Read more
                            </Button>
                        )}
                        {isExpanded && (
                            <Button
                            className="read-more-btn"
                            size="small"
                            variant="text"
                            sx={{
                              color: '#bbb',
                              textTransform: 'none',
                              fontWeight: 500,
                              p: 0,
                              minWidth: 0,
                              my: 1,
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              lineHeight: 1,
                              display: 'inline-block'
                            }}
                            >
                            Show less
                            </Button>
                        )}
                        <Typography sx={reviewFooterStyles}>{review.footer}</Typography>
                        </Box>
                    );
                    })}
                </Box>
                <IconButton
                  aria-label="next"
                  onClick={handleNext}
                  sx={{
                    color: '#fff',
                    mx: 1,
                    position: { xs: 'absolute', sm: 'relative' },
                    // overlap the review box and extend further outside the edge
                    right: { xs: -20, sm: 'auto' },
                    top: { xs: '50%', sm: 'auto' },
                    transform: { xs: 'translateY(-50%)', sm: 'none' },
                    zIndex: 3,
                    // make circular background transparent on mobile
                    bgcolor: { xs: 'transparent', sm: 'transparent' },
                    '&:hover': { bgcolor: { xs: 'transparent', sm: 'transparent' } },
                  }}
                >
                  <ArrowForwardIosIcon fontSize="large" />
                </IconButton>
                </Box>

                {/* Read More Reviews Button */}
                <Button
                variant="outlined"
                size="large"
                href="https://www.google.com/search?q=kingdom+mma&oq=kingdom+mma&gs_lcrp=EgZjaHJvbWUqFQgAEAAYQxiDARjjAhixAxiABBiKBTIVCAAQABhDGIMBGOMCGLEDGIAEGIoFMhgIARAuGEMYgwEYrwEYxwEYsQMYgAQYigUyBwgCEAAYgAQyBggDEEUYQDIHCAQQABiABDIGCAUQRRg8MgYIBhBFGDwyBggHEEUYPNIBCDE2ODJqMGo0qAIAsAIB&sourceid=chrome&ie=UTF-8#lrd=0x53a018ec1685e3bd:0xea05a72729ff2315,1,,,,"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    borderColor: '#888',
                    color: '#ccc',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    px: 3,
                    py: 1.4,
                    borderRadius: 3,
                    '&:hover': {
                    borderColor: '#fff',
                    color: '#fff',
                    backgroundColor: '#222',
                    },
                }}
                >
                See All Google Reviews
                </Button>
            </Box>
        </motion.div>

    </Box>
  );
}

export default Home;
